import { Trans, useLingui } from '@lingui/react/macro';
import { Formik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Pages } from 'modules/navigation/domain/enums/pages';
import { KitElement } from 'shared/kit/kit-element';
import { KIT_PATTERN_COLOR_COUNT } from 'shared/kit/kit-pattern';
import { ALL_KIT_TYPES, isKitType, KitType } from 'shared/kit/kit-type';
import { TeamViewTab } from 'modules/teams/domain/enums/team-view-tab';
import { Team } from 'modules/teams/domain/interfaces/team';
import { upsertTeamKits } from 'modules/teams/store/teams';
import { useSportKitElements } from 'modules/teams/ui/hooks/use-sport-kit-elements';
import { usePreventNavigation } from 'shared/hooks';
import { deriveAsyncState } from 'shared/utils/async-state';
import { AppDispatch } from 'store/store';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';

import KitEditorSkeleton from './kit-editor-skeleton';
import KitForm from './kit-form';
import { buildInitialValues, KitFormValues } from './kit-state';
import KitTypeTabs from './kit-type-tabs';

interface KitEditorProps {
  team: Team;
}

/**
 * Вкладка «Форма команды»: загрузка элементов вида спорта и четыре комплекта
 * в одной Formik-форме.
 */
const KitEditor: React.FC<KitEditorProps> = ({ team }) => {
  const { elements, isLoading, isError, hasLoaded } = useSportKitElements(team.sportType);

  if (!team.sportType) {
    return (
      <div className="py-6">
        <UDText type="body-text">
          <Trans>Тип спорта не определён для данной команды.</Trans>
        </UDText>
      </div>
    );
  }

  const state = deriveAsyncState({
    itemsCount: elements.length,
    hasCurrentData: hasLoaded,
    isFetching: isLoading,
    isError,
  });

  return (
    <UDAsyncContent
      state={state}
      minHeight={240}
      // Вместо общего спиннера — скелетон в раскладке самой формы: содержимое
      // не прыгает, когда элементы вида спорта доезжают.
      loading={<KitEditorSkeleton />}
      error={
        <UDText type="body-text">
          <Trans>Не удалось загрузить элементы формы.</Trans>
        </UDText>
      }
      empty={
        <UDText type="body-text">
          <Trans>Для данного вида спорта элементы формы не определены.</Trans>
        </UDText>
      }
    >
      {/* `key` пересоздаёт форму, когда меняется набор элементов: `initialValues`
          считаются один раз и дальше живут своей жизнью. */}
      <KitEditorForm key={elements.join(',')} team={team} elements={elements} />
    </UDAsyncContent>
  );
};

interface KitFormRouteProps {
  elements: KitElement[];
}

/**
 * Поля комплекта, взятого из адреса. Неизвестный сегмент — не ошибка страницы,
 * а повод вернуть пользователя на домашний комплект.
 */
const KitFormRoute: React.FC<KitFormRouteProps> = ({ elements }) => {
  const { kitType } = useParams<{ kitType: string }>();

  if (!isKitType(kitType)) {
    return <Navigate to={`../${KitType.HOME}`} replace />;
  }

  return <KitForm kitType={kitType} elements={elements} />;
};

interface KitEditorFormProps {
  team: Team;
  elements: KitElement[];
}

const KitEditorForm: React.FC<KitEditorFormProps> = ({ team, elements }) => {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  // Считаются один раз за монтирование: обновление команды (в том числе
  // инвалидация после сохранения) не должно стирать несохранённые правки.
  const [initialValues] = useState<KitFormValues>(() => buildInitialValues(team.kits ?? [], elements));

  // Смена комплекта меняет адрес, но не уводит с формы: спрашивать про
  // несохранённые изменения при переходе `kits/home` → `kits/away` нельзя.
  const kitsPath = Pages.TEAMS.builders.edit(team.id, TeamViewTab.KITS);
  const isSamePage = useCallback((pathname: string) => pathname.startsWith(kitsPath), [kitsPath]);

  const { PreventNavigationModal } = usePreventNavigation({
    isSamePage,
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.TEAMS.INDEX,
  });

  const handleSubmit = useCallback(
    async (values: KitFormValues) => {
      const kits = ALL_KIT_TYPES.map((kitType) => ({
        type: kitType,
        elements: elements.map((element) => {
          const { pattern, colors } = values[kitType][element];
          return {
            element,
            pattern: pattern.value,
            colors: colors.slice(0, KIT_PATTERN_COLOR_COUNT[pattern.value]),
          };
        }),
      }));

      await dispatch(upsertTeamKits({ teamId: team.id, kits })).unwrap();
    },
    [dispatch, elements, team.id],
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, formikHelpers) => {
        try {
          await handleSubmit(values);
          toast.success(t`Форма команды сохранена`);
          // Текущие значения становятся базой — иначе после инвалидации команды
          // форма считалась бы изменённой.
          formikHelpers.resetForm({ values });
          setIsFormDirty(false);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          toast.error(t`Ошибка сохранения: ${message}`);
        }
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {(formProps) => (
        <form className="flex flex-col gap-6" onSubmit={formProps.handleSubmit}>
          <KitTypeTabs elements={elements} />

          {/* Роут выбирает только, поля какого комплекта отрисовать: значения всех
              четырёх лежат в Formik выше, поэтому переход между табами не теряет правок. */}
          <Routes>
            <Route index element={<Navigate to={KitType.HOME} replace />} />
            <Route path=":kitType" element={<KitFormRoute elements={elements} />} />
            <Route path="*" element={<Navigate to={KitType.HOME} replace />} />
          </Routes>

          <div className="mb-2 flex w-1/2 justify-start pr-6">
            <UDButton
              type="submit"
              variant="primary"
              className="mr-4 w-1/2"
              disabled={!formProps.dirty}
              loading={formProps.isSubmitting}
            >
              <Trans>Сохранить</Trans>
            </UDButton>

            <UDButton
              type="button"
              variant="secondary"
              className="w-1/2"
              disabled={!formProps.dirty || formProps.isSubmitting}
              // `validate` после сброса не вызывается, поэтому флаг «форма
              // изменена» гасим руками — иначе уход со страницы спросил бы про
              // несохранённые изменения, которых уже нет.
              onClick={() => {
                formProps.resetForm();
                setIsFormDirty(false);
              }}
            >
              <Trans>Отменить</Trans>
            </UDButton>
          </div>

          <PreventNavigationModal />
        </form>
      )}
    </Formik>
  );
};

export default KitEditor;
